import { forwardRef, type InputHTMLAttributes } from 'react';
import clsx from 'clsx';
import { CheckIcon } from '@heroicons/react/20/solid';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  description?: string;
  error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, description, error, id, ...props }, ref) => {
    const checkboxId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className={clsx('relative flex items-start', className)}>
        <div className="flex h-6 items-center">
          <div className="relative">
            <input
              ref={ref}
              id={checkboxId}
              type="checkbox"
              className="peer sr-only"
              {...props}
            />
            <div
              className={clsx(
                'w-5 h-5 border-2 rounded-md transition-all duration-200 flex items-center justify-center',
                'peer-checked:bg-primary-400 peer-checked:border-primary-400',
                'peer-focus:ring-2 peer-focus:ring-primary-100 peer-focus:ring-offset-2',
                error ? 'border-danger-300' : 'border-slate-300',
                'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed'
              )}
            >
              <CheckIcon className="w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
            </div>
          </div>
        </div>

        {(label || description) && (
          <div className="ml-3">
            {label && (
              <label
                htmlFor={checkboxId}
                className={clsx(
                  'text-sm font-medium cursor-pointer',
                  error ? 'text-danger-600' : 'text-slate-700'
                )}
              >
                {label}
              </label>
            )}
            {description && <p className="text-sm text-slate-500">{description}</p>}
            {error && <p className="text-sm text-danger-500 mt-1">{error}</p>}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;

